import { StoreSkill } from '../services/skillStoreService';
import { isSkillGeneratorSkill } from '../utils/skillRunner';

export type LandingBuilderId = 'website-builder' | 'saas-builder' | 'app-builder';

export type LandingChoiceKind = 'chat' | 'builder' | 'skill';

export interface LandingSkillChoice {
  id: string;
  displayName: string;
  description: string;
  kind: LandingChoiceKind;
  skill?: StoreSkill;
}

/** Plain assistant chat: no project, no agents, just a conversation with memory. */
export const LANDING_CHAT_CHOICE: LandingSkillChoice = {
  id: 'chat',
  displayName: 'Chat',
  description: 'Ask anything. The assistant remembers you across conversations.',
  kind: 'chat',
};

export const BUILTIN_LANDING_SKILLS: LandingSkillChoice[] = [
  {
    id: 'website-builder',
    displayName: 'Website',
    description: 'Plan and build a production-ready website.',
    kind: 'builder',
  },
  {
    id: 'saas-builder',
    displayName: 'SaaS',
    description: 'Plan and build a production-ready SaaS product.',
    kind: 'builder',
  },
  {
    id: 'app-builder',
    displayName: 'App',
    description: 'Plan and build a production-ready application.',
    kind: 'builder',
  },
];

/** The tabs shown above the landing text box, in order; the Skill tab is added by the tabs component. */
export const LANDING_TABS: LandingSkillChoice[] = [...BUILTIN_LANDING_SKILLS, LANDING_CHAT_CHOICE];

/** Building is the product; the landing page starts on the SaaS builder. */
export const DEFAULT_LANDING_CHOICE: LandingSkillChoice = BUILTIN_LANDING_SKILLS[1];

export const landingChoiceAllowsMedia = (choice: LandingSkillChoice): boolean => choice.kind === 'builder';

export const landingPlaceholder = (choice: LandingSkillChoice): string => {
  if (choice.kind === 'chat') return 'Ask anything…';
  if (choice.kind === 'skill') return `Describe what you want ${choice.displayName} to do…`;

  if (choice.id === 'website-builder') return 'Describe the website you want to build…';
  if (choice.id === 'app-builder') return 'Describe the app you want to build…';

  return 'Describe your project here...';
};

export const isLandingSkillEligible = (skill: StoreSkill): boolean => (
  (skill.status === 'active' || skill.status === 'publish_failed')
  && skill.interaction_type === 'ui'
  && skill.accepts_free_text === true
);

/** Drop the skill-builder entry when the Skill Store is turned off for this deployment. */
export const withoutSkillBuilder = (skills: StoreSkill[]): StoreSkill[] => (
  skills.filter((skill) => !isSkillGeneratorSkill(skill))
);

/** Reusable skills only; the builders and chat live on the tabs, not in the skill search. */
export const buildLandingSkillChoices = (skills: StoreSkill[]): LandingSkillChoice[] => [
  ...skills
    .filter(isLandingSkillEligible)
    .map((skill) => ({
      id: skill.id,
      displayName: isSkillGeneratorSkill(skill) ? 'Build AI Skill' : skill.display_name,
      description: skill.description,
      kind: 'skill' as const,
      skill,
    })),
];
